import dayjs from "dayjs";

/** ₹ 12,34,567 — compacted above a lakh so KPI cards never wrap. */
export const fmtCurrency = (value, { compact = false } = {}) => {
    const n = Number(value);
    if (!isFinite(n) || n === 0) return "₹0";
    if (compact) {
        if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
        if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
    }
    return `₹${Math.round(n).toLocaleString("en-IN")}`;
};

export const fmtNumber = (value) => {
    const n = Number(value);
    return isFinite(n) ? n.toLocaleString("en-IN") : String(value ?? "—");
};

/** Renders a `{label, value, format}` metric coming from the backend. */
export const fmtMetric = (value, format, { compact = false } = {}) => {
    if (value === null || value === undefined || value === "") return "—";
    switch (format) {
        case "currency": return fmtCurrency(value, { compact });
        case "percent":  return `${Number(value).toFixed(Number.isInteger(Number(value)) ? 0 : 1)}%`;
        case "days":     return `${fmtNumber(value)}d`;
        case "text":     return String(value);
        default:         return fmtNumber(value);
    }
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:[T ]|$)/;
const DMY_DATE = /^\d{2}-\d{2}-\d{4}/;

/**
 * Table cells arrive in whatever shape the underlying report produced —
 * ISO dates, DD-MM-YYYY strings, decimal strings, booleans, nulls.
 * Normalise them all to something readable.
 */
export const fmtCell = (value, columnKey = "") => {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";

    if (typeof value === "string") {
        if (ISO_DATE.test(value)) {
            const d = dayjs(value);
            if (d.isValid()) return d.format("DD MMM YYYY");
        }
        if (DMY_DATE.test(value)) {
            // DD-MM-YYYY (optionally followed by a time) — reorder rather than
            // pull in the dayjs customParseFormat plugin.
            const [dd, mm, yyyy] = value.slice(0, 10).split("-");
            const d = dayjs(`${yyyy}-${mm}-${dd}`);
            return d.isValid() ? d.format("DD MMM YYYY") : value;
        }
        // Decimal strings on money columns ("125000.00")
        if (/^-?\d+(\.\d+)?$/.test(value) && isMoneyColumn(columnKey)) {
            return fmtCurrency(value);
        }
        return value;
    }

    if (typeof value === "number") {
        return isMoneyColumn(columnKey) ? fmtCurrency(value) : fmtNumber(value);
    }
    return String(value);
};

const MONEY_HINTS = ["value", "revenue", "price", "amount", "amc_value"];
export const isMoneyColumn = (key = "") =>
    MONEY_HINTS.some(hint => key.toLowerCase().includes(hint));

/** Resolve a date preset to the ISO range the unified endpoint expects. */
export const resolveRange = (preset, customFrom, customTo) => {
    const today = dayjs();
    const iso = (d) => d.format("YYYY-MM-DD");
    switch (preset) {
        case "last_week":    return { from_date: iso(today.subtract(6, "day")),  to_date: iso(today) };
        case "last_month":   return { from_date: iso(today.subtract(29, "day")), to_date: iso(today) };
        case "this_month":   return { from_date: iso(today.startOf("month")),    to_date: iso(today) };
        case "this_quarter": {
            const startMonth = Math.floor(today.month() / 3) * 3;
            return { from_date: iso(today.month(startMonth).startOf("month")), to_date: iso(today) };
        }
        case "this_year":    return { from_date: iso(today.startOf("year")),     to_date: iso(today) };
        case "custom":
            return customFrom && customTo
                ? { from_date: dayjs(customFrom).format("YYYY-MM-DD"), to_date: dayjs(customTo).format("YYYY-MM-DD") }
                : {};
        default: return {};
    }
};

export const DATE_PRESETS = [
    { value: "all",          label: "All Time" },
    { value: "last_week",    label: "Last 7 Days" },
    { value: "last_month",   label: "Last 30 Days" },
    { value: "this_month",   label: "This Month" },
    { value: "this_quarter", label: "This Quarter" },
    { value: "this_year",    label: "This Year" },
    { value: "custom",       label: "Custom Range" },
];
